/**
 * CandidateSearch - DEPRECATED JD Upload + Filter Candidate Search
 * Route: /candidate-search-legacy
 *
 * Old search page where recruiters uploaded a job description and used
 * level/location/role filters. No longer shown in navigation.
 * Superseded by InterestedFeed.tsx at /candidate-search.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Upload, Search, MapPin, Target, User, FileText, Heart, Sparkles, ChevronDown, AlertCircle, ArrowLeft, ArrowRight, Bookmark } from 'lucide-react';
import CandidateResultsList from '../components/CVSearch/CandidateResultsList';
import CandidateCVViewer from '../components/CVSearch/CandidateCVViewer';
import RateLimitNotification from '../components/RateLimitNotification';
import { useAuth } from '../hooks/useAuth';
import { handleApiError, formatErrorMessage, showToast, RateLimitInfo } from '../utils/errorHandler';
import AuthModal from '../components/Auth/AuthModal';
import { useAuthModal } from '../hooks/useAuthModal';
import { useTranslation } from 'react-i18next';
import { mapLevelValues } from '../utils/levelMapper';
import { RECRUITER_LANG_FILTER_KEY } from '../constants/recruiterLanguageFilter';

interface CVResult {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  cv_language?: string; // "en" or "de" - from Pinecone metadata
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
}

interface CVKeywords {
  cv_id: string;
  keywords: string[];
  sections: Record<string, string[]>;
}

interface CVImages {
  cv_id: string;
  images: string[];
  image_count: number;
}

interface CandidateInsights {
  fit_explanation: {
    strengths: string[];
    concerns: string[];
    fit_score: number;
    key_alignment_points: string[];
    summary: string;
  };
  job_information: {
    job_title: string;
    nice_to_know?: string;
  };
  personalized_message: {
    subject: string;
    body: string;
    key_points: string[];
  };
}

interface CandidateSearchProps {
  onSwitchToInterested?: () => void;
}

const CandidateSearch: React.FC<CandidateSearchProps> = ({ onSwitchToInterested }) => {
  const { t, i18n } = useTranslation('hire');
  const { apiCall, user, authReady } = useAuth();
  const navigate = useNavigate();
  const { showAuthModal, setShowAuthModal, requireAuth, isSignedIn } = useAuthModal();
  
  // Level options for dropdown
  const levelOptions = [
    { value: "High School Student", labelKey: "levels.highSchool", shortKey: "levelsShort.highSchool" },
    { value: "Apprentice / Vocational Training", labelKey: "levels.apprentice", shortKey: "levelsShort.apprentice" },
    { value: "Bachelor / Undergraduate Student", labelKey: "levels.bachelor", shortKey: "levelsShort.bachelor" },
    { value: "Master / Graduate Student", labelKey: "levels.master", shortKey: "levelsShort.master" },
    { value: "Working Student / Intern", labelKey: "levels.workingStudent", shortKey: "levelsShort.workingStudent" },
    { value: "PhD Student", labelKey: "levels.phd", shortKey: "levelsShort.phd" },
    { value: "Qualified Worker (after apprenticeship)", labelKey: "levels.qualifiedWorker", shortKey: "levelsShort.qualifiedWorker" },
    { value: "Entrepreneur / Self-Employed", labelKey: "levels.entrepreneur", shortKey: "levelsShort.entrepreneur" },
    { value: "Junior / Entry-Level / New Grad (0-2 years)", labelKey: "levels.junior", shortKey: "levelsShort.junior" },
    { value: "Mid-Level (2-5 years)", labelKey: "levels.midLevel", shortKey: "levelsShort.midLevel" },
    { value: "Senior (5+ years)", labelKey: "levels.senior", shortKey: "levelsShort.senior" },
    { value: "Master (Baker, Hairstylist, Carpenter, Electrician, etc.)", labelKey: "levels.masterCraftsman", shortKey: "levelsShort.masterCraftsman" },
    { value: "Lead / Management", labelKey: "levels.lead", shortKey: "levelsShort.lead" },
    { value: "Expert / Principal (15+ years)", labelKey: "levels.expert", shortKey: "levelsShort.expert" }
  ];
  
  const [profile, setProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [jobDescriptionFile, setJobDescriptionFile] = useState<File | null>(null);
  const [convertedPages, setConvertedPages] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [jobLocations, setJobLocations] = useState('');
  const [jobRoleDescription, setJobRoleDescription] = useState('');
  const [careerStatus, setCareerStatus] = useState<string>('curious,actively_seeking');
  const [customPrompt, setCustomPrompt] = useState('');
  const [targetedRole, setTargetedRole] = useState('');
  const [targetedLevel, setTargetedLevel] = useState<string[]>([]);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const levelDropdownRef = useRef<HTMLDivElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<CVResult[]>([]);
  const [allResults, setAllResults] = useState<CVResult[]>([]); // Unfiltered results for re-filtering
  const [selectedCV, setSelectedCV] = useState<string | null>(null);
  
  // Load language filter from localStorage or use default (both selected)
  const [languageFilter, setLanguageFilter] = useState<{ de: boolean; en: boolean }>(() => {
    const saved = localStorage.getItem(RECRUITER_LANG_FILTER_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { de: true, en: true };
      }
    }
    return { de: true, en: true };
  });
  const [cvKeywords, setCvKeywords] = useState<Record<string, CVKeywords>>({});
  const [cvImages, setCvImages] = useState<Record<string, CVImages>>({});
  const [cvInsights, setCvInsights] = useState<Record<string, CandidateInsights>>({});
  const [cvInsightsHashes, setCvInsightsHashes] = useState<Record<string, string>>({});
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [reachOutStatusMap, setReachOutStatusMap] = useState<Record<string, { status: string; reach_out_id: string; conversation_id?: string }>>({});
  const [savedCvIds, setSavedCvIds] = useState<Set<string>>(new Set());
  const [skipSaved, setSkipSaved] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const loadSavedCvIds = useCallback(async () => {
    if (!user?.userId || !authReady) return;
    try {
      const resp = await apiCall(`${apiBaseUrl}/api/v1/profiles/me/saved-cvs`);
      if (resp.ok) {
        const data = await resp.json();
        const ids = new Set<string>((data.saved_cvs || []).map((s: { cv_id: string }) => s.cv_id));
        setSavedCvIds(ids);
      }
    } catch {
      /* ignore */
    }
  }, [user?.userId, authReady, apiCall, apiBaseUrl]);

  const canUseSavedCvs = !!(user && authReady && profile?.is_recruiter && !isBlocked);

  const toggleSaveCandidate = async (cvId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canUseSavedCvs) return;
    const wasSaved = savedCvIds.has(cvId);
    const nextIds = new Set(savedCvIds);
    if (wasSaved) nextIds.delete(cvId);
    else nextIds.add(cvId);
    setSavedCvIds(nextIds);

    try {
      if (wasSaved) {
        await apiCall(`${apiBaseUrl}/api/v1/profiles/me/saved-cvs/${cvId}`, { method: 'DELETE' });
      } else {
        await apiCall(`${apiBaseUrl}/api/v1/profiles/me/saved-cvs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cv_id: cvId, prompt: '' }),
        });
      }
    } catch {
      setSavedCvIds((prev) => {
        const reverted = new Set(prev);
        if (wasSaved) reverted.add(cvId);
        else reverted.delete(cvId);
        return reverted;
      });
    }
  };

  // Persist language filter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(RECRUITER_LANG_FILTER_KEY, JSON.stringify(languageFilter));
  }, [languageFilter]);

  // Helper function to filter results by language (memoized for stability)
  // Also filters out 'invisible' career_status as safety layer (should never reach frontend, but extra protection)
  const applyLanguageFilter = useCallback((results: CVResult[], filter: { de: boolean; en: boolean }): CVResult[] => {
    // Safety filter: Always exclude invisible profiles (extra protection layer)
    const safeResults = results.filter((result: CVResult) => {
      if (result.career_status === 'invisible') {
        console.warn('⚠️ CandidateSearch: Filtered out invisible profile from results:', result.cv_id);
        return false;
      }
      return true;
    });
    
    // If both unselected → show empty (user needs to select at least one)
    if (!filter.de && !filter.en) {
      return [];
    }
    
    // If both selected → show all (already filtered for safety)
    if (filter.de && filter.en) {
      return safeResults;
    }
    
    // Filter by selected language(s)
    return safeResults.filter((result: CVResult) => {
      if (filter.de && result.cv_language === 'de') return true;
      if (filter.en && result.cv_language === 'en') return true;
      return false;
    });
  }, []);

  // Re-filter results when language filter changes
  useEffect(() => {
    if (allResults.length > 0) {
      const filteredResults = applyLanguageFilter(allResults, languageFilter);
      setResults(filteredResults);
    }
  }, [languageFilter, allResults, applyLanguageFilter]);

  // Merge reach-out status into results for display
  const resultsWithStatus = useMemo(() => {
    let mapped = results.map((r) => {
      const sm = reachOutStatusMap[r.cv_id];
      return sm ? { ...r, reach_out_status: sm.status, reach_out_id: sm.reach_out_id, conversation_id: sm.conversation_id } : r;
    });
    if (skipSaved && savedCvIds.size > 0) mapped = mapped.filter((r) => !savedCvIds.has(r.cv_id));
    return mapped;
  }, [results, reachOutStatusMap, skipSaved, savedCvIds]);

  /** One bootstrap fetch per user - avoids duplicate /saved-cvs if deps ever churn */
  const savedCvBootstrapUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.userId) {
      savedCvBootstrapUserRef.current = null;
      return;
    }
    if (!canUseSavedCvs) return;
    if (savedCvBootstrapUserRef.current === user.userId) return;
    savedCvBootstrapUserRef.current = user.userId;
    void loadSavedCvIds();
  }, [canUseSavedCvs, user?.userId, loadSavedCvIds]);

  const loadSavedCvIdsRef = useRef(loadSavedCvIds);
  loadSavedCvIdsRef.current = loadSavedCvIds;

  // Load recruiter profile on mount - deps must NOT include loadSavedCvIds (identity churn caused 429 loops)
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.userId) {
        setIsLoadingProfile(false);
        return;
      }
      
      console.debug('🔄 CandidateSearch: Loading recruiter profile for:', user.userId);
      
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
        
        if (response.ok) {
          const data = await response.json();
          console.debug('✅ CandidateSearch: Profile loaded:', data);
          
          // Mark as blocked for explorers (shows banner instead of modal)
          if (!data.is_recruiter) {
            setIsBlocked(true);
            setIsLoadingProfile(false);
            return;
          }
          
          setProfile(data);
        } else {
          console.error('❌ CandidateSearch: Failed to load profile, status:', response.status);
        }
      } catch (error) {
        console.error('❌ CandidateSearch: Failed to load recruiter profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    
    // Load on mount (always fresh!)
    loadProfile();
    
    // Reload when user returns to page (e.g., after completing profile)
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.userId) {
        console.debug('👁️ CandidateSearch: Tab became visible, reloading profile...');
        loadProfile();
        void loadSavedCvIdsRef.current();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.userId, apiCall]);

  // Check if recruiter profile is incomplete (only for recruiters!)
  const isProfileIncomplete = profile && profile.is_recruiter && (
    !profile.first_name || 
    !profile.last_name || 
    !profile.company_name || 
    !profile.recruiter_background ||
    !profile.consents?.recruiter_profile_visibility?.granted
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(event.target as Node)) {
        setIsLevelDropdownOpen(false);
      }
    };

    if (isLevelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLevelDropdownOpen]);

  const handleLabelClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    if (!isSignedIn) {
      e.preventDefault(); // Block file picker
      setShowAuthModal(true);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Block explorer - banner is already visible at top
    if (isBlocked || !profile?.is_recruiter) {
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    setJobDescriptionFile(file);
    setConvertedPages([]);
    
    // If it's a PDF, convert to images
    if (file.type === 'application/pdf') {
      setIsConverting(true);
      try {
        const pages = await convertPDFToImages(file);
        setConvertedPages(pages);
      } catch (error) {
        console.error('PDF conversion failed:', error);
        showToast(t('errors.pdfConversionFailed'), 'error');
      } finally {
        setIsConverting(false);
      }
    }
  };

  const convertPDFToImages = async (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // @ts-ignore - pdfjsLib is loaded from CDN
          const pdf = await pdfjsLib.getDocument({ data: e.target?.result }).promise;
          const pages: string[] = [];
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.5 });
            
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            await page.render({
              canvasContext: context!,
              viewport: viewport
            }).promise;
            
            pages.push(canvas.toDataURL('image/png'));
          }
          
          resolve(pages);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSearch = async () => {
    // Check auth first
    if (!requireAuth()) {
      return; // Auth modal will be shown
    }

    // Block explorer - banner is already visible at top
    if (isBlocked || !profile?.is_recruiter) {
      return;
    }

    if (!jobDescriptionFile && !customPrompt.trim() && !jobRoleDescription.trim()) {
      showToast(t('errors.noInputProvided'), 'error');
      return;
    }

    if (isConverting) {
      showToast(t('errors.waitForConversion'), 'error');
      return;
    }

    setIsSearching(true);
    setResults([]);
    setAllResults([]); // Clear unfiltered results too
    setSelectedCV(null);
    setRateLimitInfo(null); // Clear previous rate limit info
    setReachOutStatusMap({}); // Clear reach-out status for new search

    try {
      const formData = new FormData();
      formData.append('user_id', 'test_user');
      
      if (jobDescriptionFile) {
        // If we have converted pages from PDF, use those
        if (convertedPages.length > 0) {
          for (let i = 0; i < convertedPages.length; i++) {
            const base64 = convertedPages[i];
            const response = await fetch(base64);
            const blob = await response.blob();
            const file = new File([blob], `page${i + 1}.png`, { type: 'image/png' });
            
            if (i === 0) {
              formData.append('page1', file);
            } else if (i === 1) {
              formData.append('page2', file);
            } else if (i === 2) {
              formData.append('page3', file);
            }
          }
        } else {
          // Use original file (PNG/JPG)
          formData.append('page1', jobDescriptionFile);
        }
      }
      
      if (jobLocations.trim()) {
        formData.append('job_locations', jobLocations);
      }
      
      if (jobRoleDescription.trim()) {
        formData.append('job_role_requirements', jobRoleDescription);
      }
      
      if (careerStatus) {
        formData.append('career_status', careerStatus);
      }
      
      if (customPrompt.trim()) {
        formData.append('custom_prompt', customPrompt);
      }
      
      if (targetedRole.trim()) {
        formData.append('targeted_role', targetedRole);
      }
      
      if (targetedLevel.length > 0) {
        // Map levels to current language (EN or DE) before sending to backend
        const currentLang = i18n.language.startsWith('de') ? 'de' : 'en';
        const mappedLevels = mapLevelValues(targetedLevel, currentLang);
        formData.append('targeted_level', mappedLevels.join(', '));
      }
      
      // Add required user_id
      formData.append('user_id', 'test_user_debug');

      const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/search/candidates`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        console.debug('🔍 DEBUG: Backend response:', data);
        console.debug('🔍 DEBUG: First result keywords:', data.results?.[0]?.keywords);
        
        // Store all results (unfiltered) - useEffect will handle filtering
        const backendResultsRaw = data.results || [];
        const seenIds = new Set<string>();
        const backendResults = (backendResultsRaw as CVResult[]).filter((r) => {
          const id = r.cv_id;
          if (!id || seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });
        if (backendResults.length < backendResultsRaw.length) {
          console.warn(
            '⚠️ CandidateSearch: Removed duplicate cv_id entries from search response:',
            (backendResultsRaw as CVResult[]).length - backendResults.length
          );
        }
        setAllResults(backendResults);
        
        // Fetch reach-out status for these CVs (recruiter only, non-blocking)
        const cvIds = backendResults.map((r) => r.cv_id).filter(Boolean);
        if (cvIds.length > 0 && profile?.is_recruiter) {
          try {
            const statusResp = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/companies/reach-out-status-by-cv-ids`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ cv_ids: cvIds }),
            });
            if (statusResp.ok) {
              const statusData = await statusResp.json();
              setReachOutStatusMap(statusData.status_map || {});
            }
          } catch {
            setReachOutStatusMap({});
          }
        } else {
          setReachOutStatusMap({});
        }
        
        // Clear previous CV data for fresh search
        setCvImages({});
        setCvKeywords({});
        setSelectedCV(null);
      } else {
        // Handle API errors with proper error handler
        const apiError = await handleApiError(response);
        
        if (apiError.status === 429 && apiError.rateLimitInfo) {
          // Show rate limit notification component
          setRateLimitInfo(apiError.rateLimitInfo);
        } else {
          // Show toast for other errors
          showToast(apiError.message, 'error', apiError.retryAfterSeconds ? apiError.retryAfterSeconds * 1000 : 5000);
        }
      }

    } catch (error: any) {
      console.error('Search error:', error);
      showToast(t('errors.searchFailed'), 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const loadCVData = async (cvId: string) => {
    // Load images and keywords only when CV is selected (lazy loading)
    try {
      // Load images with retry logic (no track_view - only Explore counts views)
      const imagesResponse = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/cv/${cvId}/images`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      if (imagesResponse.ok) {
        const imagesData = await imagesResponse.json();
        setCvImages(prev => ({
          ...prev,
          [cvId]: imagesData
        }));
      }

      // Load keywords (with include_keywords=false to skip expensive keyword extraction)
      // Keywords are no longer displayed in CV details view, so we skip the extraction
      // This saves ~200-400ms latency + blob storage read costs
      const keywordsResponse = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/cv/${cvId}/content?include_keywords=false`, {
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });
      if (keywordsResponse.ok) {
        const contentData = await keywordsResponse.json();
        // Extract keywords from metadata (will be empty array due to include_keywords=false)
        const keywordsData = {
          cv_id: cvId,
          keywords: contentData.metadata?.keywords || [],
          sections: contentData.metadata?.sections_keywords || {}
        };
        setCvKeywords(prev => ({
          ...prev,
          [cvId]: keywordsData
        }));
      }
    } catch (error: any) {
      console.error(`Failed to load data for CV ${cvId}:`, error);
      // Don't show toast for background data loading failures
      // User can click again to retry
    }
  };

  const handleCVSelect = (cvId: string) => {
    setSelectedCV(cvId);
    // Load CV data only when selected (lazy loading)
    if (!cvImages[cvId] || !cvKeywords[cvId]) {
      loadCVData(cvId);
    }
    // Preload next 1-2 candidates for smooth navigation
    const idx = resultsWithStatus.findIndex((r) => r.cv_id === cvId);
    for (let i = 1; i <= 2; i++) {
      if (idx + i < resultsWithStatus.length) {
        const nextId = resultsWithStatus[idx + i].cv_id;
        if (!cvImages[nextId] || !cvKeywords[nextId]) loadCVData(nextId);
      }
      if (idx - i >= 0) {
        const prevId = resultsWithStatus[idx - i].cv_id;
        if (!cvImages[prevId] || !cvKeywords[prevId]) loadCVData(prevId);
      }
    }
  };

  const cvIdx = selectedCV ? resultsWithStatus.findIndex((r) => r.cv_id === selectedCV) : -1;
  const hasPrevCV = cvIdx > 0;
  const hasNextCV = cvIdx >= 0 && cvIdx < resultsWithStatus.length - 1;

  const navigateCV = useCallback((dir: 'prev' | 'next') => {
    const idx = resultsWithStatus.findIndex((r) => r.cv_id === selectedCV);
    const nextIdx = dir === 'prev' ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= resultsWithStatus.length) return;
    const nextId = resultsWithStatus[nextIdx].cv_id;
    handleCVSelect(nextId);
  }, [resultsWithStatus, selectedCV]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedCV) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigateCV('prev'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateCV('next'); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedCV, navigateCV]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col">
      {/* Explorer Nudge Banner - shows at top for logged-in explorers */}
      {isBlocked && (
        <div className="bg-white/90 backdrop-blur-lg border-b border-slate-300 shadow-sm">
          <div className="max-w-6xl mx-auto px-3 py-3 sm:px-6 sm:py-4">
            <div className="bg-gradient-to-br from-yellow-50 to-pink-100 p-3 sm:p-4 rounded-lg border border-pink-200 shadow-sm">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-1.5 sm:gap-3">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-sm sm:text-lg">🌟</span>
                  <div>
                    <h3 className="text-xs sm:text-sm font-semibold text-pink-800">
                      {i18n.language === 'de' ? 'Möchtest du gefunden werden?' : 'Want to get discovered?'}
                    </h3>
                    <p className="text-[11px] sm:text-sm text-pink-600 leading-snug">
                      {i18n.language === 'de'
                        ? 'Lade deinen CV hoch und setze deine Job-Präferenzen auf der Inspire-Seite!'
                        : 'Upload your CV and set job preferences on the Inspire page!'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/?tab=inspire')}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-sm whitespace-nowrap"
                >
                  {i18n.language === 'de' ? 'CV hochladen' : 'Upload my CV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Limit Notification */}
      {rateLimitInfo && (
        <RateLimitNotification 
          rateLimitInfo={rateLimitInfo}
          feature="candidate_search"
          onClose={() => setRateLimitInfo(null)}
        />
      )}
      
      {/* Search Header */}
      <div className="bg-white/90 backdrop-blur-lg border-b border-slate-300 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-5">
          <div className="text-center">
            <h1 className="text-xl sm:text-[1.65rem] font-bold text-gray-900 mb-1.5 sm:mb-2 leading-tight sm:leading-normal">
              {t('header.title').split('<highlight>')[0]}
              <span className="bg-gradient-to-r from-rose-500 via-rose-600 to-rose-700 bg-clip-text text-transparent">
                {t('header.title').match(/<highlight>(.*?)<\/highlight>/)?.[1]}
              </span>
              {t('header.title').split('</highlight>')[1]}
            </h1>
          </div>
          {onSwitchToInterested && (
            <div className="flex justify-center mt-1.5 sm:mt-2">
              <button
                onClick={onSwitchToInterested}
                className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-pink-50 border border-pink-200 text-pink-600 hover:border-pink-400 hover:bg-pink-100 transition-all max-w-[calc(100vw-1.5rem)]"
              >
                <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-pink-500 flex-shrink-0" />
                <span className="truncate">Interested Feed</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Incomplete Banner */}
      {isProfileIncomplete && (
        <div className="max-w-6xl mx-auto mt-3 sm:mt-8 px-3 sm:px-6">
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3 sm:p-6 text-center shadow-md">
            <div className="flex items-center justify-center mb-1.5 sm:mb-3">
              <AlertCircle className="text-yellow-600 mr-1.5 sm:mr-2 flex-shrink-0" size={18} />
              <h3 className="text-sm sm:text-lg font-semibold text-yellow-900 text-left sm:text-center">
                {t('profileIncomplete.title')}
              </h3>
            </div>
            <p className="text-yellow-800 mb-3 sm:mb-4 text-xs sm:text-base leading-snug">
              {t('profileIncomplete.description')}
            </p>
            <button 
              onClick={() => navigate('/recruiter-profile')}
              className="bg-yellow-600 text-white px-4 py-2 sm:px-6 text-sm rounded-lg hover:bg-yellow-700 transition-colors font-medium w-full sm:w-auto"
            >
              {t('profileIncomplete.button')}
            </button>
          </div>
        </div>
      )}

      {/* Search Form */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto py-2 sm:py-4 px-2.5 sm:px-6">
          <div className="flex flex-col md:flex-row gap-2 md:gap-4">
            {/* Job Description Upload */}
            <motion.div 
              className="w-full md:w-2/5 bg-white/80 backdrop-blur-lg rounded-lg sm:rounded-xl p-2.5 sm:p-4 shadow-sm border border-slate-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-2 sm:mb-3">
                <span className="text-sm sm:text-lg mr-1.5 sm:mr-2">📄</span>
                <h2 className="text-sm sm:text-base font-semibold text-gray-900">
                  {t('jobDescription.title')}
                </h2>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-2.5 sm:p-3 text-center hover:border-gray-600 hover:bg-gray-100 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="job-description-upload"
                  />
                  <label 
                    htmlFor="job-description-upload" 
                    className="cursor-pointer"
                    onClick={handleLabelClick}
                  >
                    <div className="mb-1 sm:mb-1.5">
                      {jobDescriptionFile ? (
                        <span className="text-xl sm:text-2xl">📄</span>
                      ) : (
                        <Upload className="text-slate-400 mx-auto w-5 h-5 sm:w-6 sm:h-6" />
                      )}
                    </div>
                    <p className="text-slate-600 font-medium text-xs sm:text-sm">
                      {jobDescriptionFile ? jobDescriptionFile.name : t('jobDescription.upload')}
                      {isConverting && ` (${t('jobDescription.convertingPdf')})`}
                    </p>
                    <p className="text-slate-400 text-[11px] sm:text-xs mt-0.5 sm:mt-1">
                      {t('jobDescription.formats')}
                    </p>
                  </label>
                </div>
                
                {jobDescriptionFile && !isConverting && (
                  <motion.div 
                    className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <p className="text-green-700 text-xs sm:text-sm">
                      ✅ {jobDescriptionFile.name} {t('jobDescription.uploaded')}
                      {convertedPages.length > 0 && ` (${convertedPages.length} ${t('jobDescription.pagesConverted')})`}
                    </p>
                  </motion.div>
                )}
                
                {isConverting && (
                  <motion.div 
                    className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-b-2 border-blue-600 mr-1.5 sm:mr-2"></div>
                      <p className="text-blue-700 text-xs sm:text-sm">
                        {t('jobDescription.converting')}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Job Requirements */}
            <motion.div 
              className="w-full md:w-3/5 bg-white/80 backdrop-blur-lg rounded-lg sm:rounded-xl p-2.5 sm:p-4 shadow-sm border border-slate-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center mb-2 sm:mb-3">
                <span className="text-sm sm:text-lg mr-1.5 sm:mr-2">🎯</span>
                <h2 className="text-sm sm:text-base font-semibold text-gray-900">
                  {t('requirements.title')}
                </h2>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {/* Career Status */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                    <User className="inline mr-0.5 align-text-bottom" size={12} />
                    {t('requirements.careerStatus.label')}
                  </label>
                  <div className="relative">
                    <select
                      value={careerStatus}
                      onChange={(e) => setCareerStatus(e.target.value)}
                      className="w-full px-2 py-1.5 sm:px-2.5 text-xs sm:text-sm border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 appearance-none bg-white pr-8"
                    >
                      <option value="curious,actively_seeking">{t('requirements.careerStatus.allCandidates')}</option>
                      <option value="actively_seeking">{t('requirements.careerStatus.ready')}</option>
                      <option value="curious">{t('requirements.careerStatus.curious')}</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                  </div>
                </div>

                {/* Job Locations */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                    <MapPin className="inline mr-0.5 align-text-bottom" size={12} />
                    {t('requirements.locations.label')}
                  </label>
                  <input
                    type="text"
                    value={jobLocations}
                    onChange={(e) => setJobLocations(e.target.value)}
                    placeholder={t('requirements.locations.placeholder')}
                    className="w-full px-2 py-1.5 sm:px-2.5 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 text-xs sm:text-sm"
                    maxLength={500}
                  />
                </div>

                {/* Job/Role Description */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                    <Heart className="inline mr-0.5 align-text-bottom" size={12} />
                    {t('requirements.roleInterests.label')}
                  </label>
                  <textarea
                    value={jobRoleDescription}
                    onChange={(e) => setJobRoleDescription(e.target.value)}
                    placeholder={t('requirements.roleInterests.placeholder')}
                    rows={2}
                    maxLength={500}
                    className="w-full px-2 py-1.5 sm:px-2.5 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none text-xs sm:text-sm"
                  />
                </div>

                {/* Targeted Role/Level Search */}
                <div className="flex flex-col lg:flex-row lg:items-end gap-2 lg:gap-3">
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                      <Target className="inline mr-0.5 align-text-bottom" size={12} />
                      {t('requirements.currentRole.label')}
                    </label>
                    <input
                      type="text"
                      value={targetedRole}
                      onChange={(e) => setTargetedRole(e.target.value)}
                      placeholder={t('requirements.currentRole.placeholder')}
                      className="w-full h-8 sm:h-9 px-2 sm:px-2.5 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 text-xs sm:text-sm box-border"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] sm:text-xs font-medium text-gray-700 mb-0.5 sm:mb-1">
                      <Target className="inline mr-0.5 align-text-bottom" size={12} />
                      {t('results.currentLevel')}
                    </label>
                    <div className="relative" ref={levelDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)}
                        className="w-full h-8 sm:h-9 px-2 sm:px-2.5 text-xs sm:text-sm border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 text-left bg-white flex items-center justify-between box-border"
                      >
                        <span className="text-gray-700 truncate pr-1">
                          {targetedLevel.length === 0 ? t('requirements.selectLevels.button') : `${targetedLevel.length} ${t('requirements.selectLevels.selected')}`}
                        </span>
                        <ChevronDown className="text-gray-400 flex-shrink-0" size={14} />
                      </button>
                      
                      {isLevelDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {levelOptions.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={targetedLevel.includes(option.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setTargetedLevel([...targetedLevel, option.value]);
                                  } else {
                                    setTargetedLevel(targetedLevel.filter(l => l !== option.value));
                                  }
                                }}
                                className="mr-1.5 sm:mr-2"
                              />
                              <span className="text-[11px] sm:text-sm text-gray-600 sm:text-gray-500 leading-snug">{t(option.labelKey)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      
                      {targetedLevel.length > 0 && (
                        <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                          {targetedLevel.map((level) => {
                            const option = levelOptions.find(opt => opt.value === level);
                            const label = option ? t(option.shortKey) : level;
                            
                            return (
                              <span
                                key={level}
                                className="inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-600"
                              >
                                {label}
                                <button
                                  type="button"
                                  onClick={() => setTargetedLevel(targetedLevel.filter(l => l !== level))}
                                  className="ml-0.5 sm:ml-1 text-gray-500 hover:text-gray-700"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Custom Prompt */}
                <div className="bg-gradient-to-br from-yellow-50 to-pink-100 p-2 sm:p-3 rounded-lg border border-pink-200 shadow-sm">
                  <h3 className="text-[11px] sm:text-xs font-semibold text-pink-800 mb-1.5 sm:mb-2 flex items-center gap-1">
                    <FileText className="inline flex-shrink-0" size={12} />
                    {t('requirements.customRequirements.title')}
                  </h3>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder={t('requirements.customRequirements.placeholder')}
                    rows={2}
                    className="w-full px-2 py-1.5 sm:px-2.5 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none text-xs sm:text-sm"
                    maxLength={500}
                  />
                  <p className="text-[10px] sm:text-[11px] text-pink-600 mt-1 sm:mt-1.5 leading-snug">
                    {t('requirements.customRequirements.hint')}
                  </p>
                </div>

                {/* Search Button */}
                <button
                  onClick={handleSearch}
                  disabled={isProfileIncomplete || isSearching || isConverting || (!jobDescriptionFile && !customPrompt.trim() && !jobRoleDescription.trim())}
                  className={`w-full py-2 sm:py-2.5 px-3 rounded-lg text-xs sm:text-sm font-semibold text-white
                             transition-all duration-200 sm:transform sm:hover:scale-[1.02]
                             ${isProfileIncomplete || isSearching || isConverting || (!jobDescriptionFile && !customPrompt.trim() && !jobRoleDescription.trim())
                               ? 'bg-slate-300 cursor-not-allowed'
                               : 'bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 shadow-lg hover:shadow-xl'
                             }`}
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1.5"></div>
                      <span className="text-xs sm:text-sm">{t('jobDescription.convertingPdf')}</span>
                    </div>
                  ) : isSearching ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1.5"></div>
                      <span className="text-xs sm:text-sm">{t('requirements.searching')}</span>
                    </div>
                  ) : (
                    <Search className="mx-auto" size={16} />
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex flex-1 w-full max-w-6xl mx-auto" style={{ minHeight: 'calc(100vh - 280px)' }}>
        {/* Results Sidebar - Hidden on mobile/iPad when CV is selected */}
        <div className={`w-full lg:w-2/5 border-r border-slate-300 bg-white/70 shadow-sm ${selectedCV ? 'hidden lg:block' : 'block'}`}>
          <CandidateResultsList
            results={resultsWithStatus}
            selectedCV={selectedCV}
            onCVSelect={handleCVSelect}
            cvKeywords={cvKeywords}
            isLoading={isSearching}
            languageFilter={languageFilter}
            onLanguageFilterChange={setLanguageFilter}
            hasSearched={allResults.length > 0}
            showSave={canUseSavedCvs}
            savedCvIds={savedCvIds}
            onToggleSave={toggleSaveCandidate}
            skipSaved={skipSaved}
            onSkipSavedChange={setSkipSaved}
          />
        </div>
        
        {/* CV Viewer - Hidden on mobile/iPad when no CV is selected */}
        <div className={`w-full lg:w-3/5 bg-white/90 shadow-sm flex flex-col ${selectedCV ? 'block' : 'hidden lg:block'}`}>
          {selectedCV && (
            <div className="flex-shrink-0 flex items-center justify-between px-2 sm:px-4 py-1.5 sm:py-2 border-b border-slate-200 bg-white/95 gap-1">
              <button
                type="button"
                onClick={() => navigateCV('prev')}
                disabled={!hasPrevCV || resultsWithStatus.length <= 1}
                className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg transition-all touch-manipulation active:scale-[0.95] ${
                  hasPrevCV && resultsWithStatus.length > 1 ? 'text-slate-600 hover:bg-slate-100 hover:text-pink-500' : 'text-slate-300 cursor-default'
                }`}
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => setSelectedCV(null)}
                  className="text-[11px] sm:text-xs text-slate-500 hover:text-pink-500 font-medium transition-colors"
                >
                  {t('cvViewer.backToList', 'Zu Liste')}
                </button>
                {resultsWithStatus.length > 1 && (
                  <span className="text-[10px] sm:text-xs text-slate-400 tabular-nums">{cvIdx + 1}/{resultsWithStatus.length}</span>
                )}
                {canUseSavedCvs && (
                  <button
                    type="button"
                    onClick={(e) => toggleSaveCandidate(selectedCV, e)}
                    title={savedCvIds.has(selectedCV) ? t('results.savedCandidate') : t('results.saveCandidate')}
                    className={`p-0.5 rounded-lg transition-all touch-manipulation flex items-center justify-center ${
                      savedCvIds.has(selectedCV)
                        ? 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                        : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${savedCvIds.has(selectedCV) ? 'fill-slate-700' : ''}`} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigateCV('next')}
                disabled={!hasNextCV || resultsWithStatus.length <= 1}
                className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg transition-all touch-manipulation active:scale-[0.95] ${
                  hasNextCV && resultsWithStatus.length > 1 ? 'text-slate-600 hover:bg-slate-100 hover:text-pink-500' : 'text-slate-300 cursor-default'
                }`}
              >
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          )}
          <AnimatePresence mode="wait">
            {selectedCV ? (
              <motion.div
                key={selectedCV}
                className="flex-1 min-h-0 overflow-auto"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <CandidateCVViewer 
                  cvId={selectedCV}
                  images={cvImages[selectedCV] || null}
                  keywords={cvKeywords[selectedCV] || null}
                  score={resultsWithStatus.find(r => r.cv_id === selectedCV)?.score || 0}
                  showSave={canUseSavedCvs}
                  isSaved={savedCvIds.has(selectedCV)}
                  onToggleSave={(e) => toggleSaveCandidate(selectedCV, e)}
                  candidateCurrentRole={resultsWithStatus.find(r => r.cv_id === selectedCV)?.current_role}
                  candidateCurrentLevel={resultsWithStatus.find(r => r.cv_id === selectedCV)?.current_level}
                  candidateBaseLocations={resultsWithStatus.find(r => r.cv_id === selectedCV)?.base_locations}
                  candidateRolePreferences={resultsWithStatus.find(r => r.cv_id === selectedCV)?.role_preferences}
                  candidatePreferredLocations={resultsWithStatus.find(r => r.cv_id === selectedCV)?.preferred_locations}
                  reachOutStatus={resultsWithStatus.find(r => r.cv_id === selectedCV)?.reach_out_status}
                  reachOutId={resultsWithStatus.find(r => r.cv_id === selectedCV)?.reach_out_id}
                  conversationId={resultsWithStatus.find(r => r.cv_id === selectedCV)?.conversation_id}
                  onReachOutSuccess={(data) => {
                    if (selectedCV) {
                      setReachOutStatusMap((prev) => ({
                        ...prev,
                        [selectedCV]: { status: 'candidate_sparked', reach_out_id: data.reach_out_id, conversation_id: data.conversation_id },
                      }));
                    }
                  }}
                  onStatusChange={(status) => {
                    if (selectedCV) {
                      setReachOutStatusMap((prev) => {
                        const cur = prev[selectedCV] || {};
                        return { ...prev, [selectedCV]: { ...cur, status } };
                      });
                    }
                  }}
                  jobDescriptionFile={jobDescriptionFile}
                  cachedInsights={cvInsights[selectedCV] || null}
                  cachedInsightsHash={cvInsightsHashes[selectedCV] || null}
                  onInsightsGenerated={(insights, hash) => {
                    setCvInsights(prev => ({ ...prev, [selectedCV]: insights }));
                    setCvInsightsHashes(prev => ({ ...prev, [selectedCV]: hash }));
                  }}
                  customRequirements={customPrompt}
                  targetedLocations={jobLocations}
                  targetedRolePreferences={jobRoleDescription}
                  targetedCurrentRole={targetedRole}
                  targetedCurrentLevel={targetedLevel.join(', ')}
                  onBack={() => setSelectedCV(null)}
                />
              </motion.div>
            ) : (
              <motion.div
                className="flex items-center justify-center h-full text-slate-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="text-center">
                  <div className="flex justify-center mb-1.5 sm:mb-2">
                    <span className="text-2xl sm:text-3xl">🔍</span>
                  </div>
                  <p className="text-xs sm:text-sm">{t('results.noSelection')}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Authentication Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

    </div>
  );
};

export default CandidateSearch;
